import { eachDayOfInterval, format } from 'date-fns';
import { DayPart, GroupOfSelectedDays, SelectedDay } from 'components/common/types/Deal.types';
import {
  DirectSalesWeeklyPattern,
  DirectSalesWeeklyPatternEntry,
  Days,
  DirectSalesNoRepeatPattern,
  DirectSalesNoRepeatPatternEntry,
  DirectSalesRangePattern,
} from 'components/common/types/DirectSalesCampaign.types';
import {
  SECONDS_IN_AN_HOUR,
  END_OFFSET_ADJUSTMENT,
  ALL_HOURS,
  ALL_DAYS_OF_THE_WEEK,
} from 'components/common/DaypartTargeting/constants';

export const transformRangePattern = (selectedDays: GroupOfSelectedDays): DirectSalesNoRepeatPattern => {
  const entries = Object.values(selectedDays)
    .filter(({ hours, isFullDay }) => hours.length || isFullDay)
    .map(({ date, hours, isFullDay }) => ({
      date: format(date, 'yyyy-MM-dd'),
      hours: isFullDay ? [] : hours,
    }));

  return {
    type: 'NO_REPEAT',
    entries,
  };
};

export const transformWeeklyPattern = (selectedDays: GroupOfSelectedDays): DirectSalesWeeklyPattern => {
  const entries = Object.values(selectedDays).reduce<DirectSalesWeeklyPatternEntry[]>(
    (days, { dayOfTheWeek, hours }) => {
      if (hours.length === 0) {
        return days;
      }

      const dayHours = hours.length === 24 ? [] : hours;

      days.push({
        day: dayOfTheWeek,
        hours: dayHours,
      });

      return days;
    },
    [],
  );

  return {
    type: 'WEEKLY',
    entries,
  };
};

export const groupHoursIntoTimeBlocks = (hours: number[]): DayPart[] => {
  const isFullDay = hours.length === 24;

  return hours.reduce((timeBlocks: DayPart[], hour: number, index: number) => {
    const startOffset = hour * SECONDS_IN_AN_HOUR;
    const endOffset = startOffset + END_OFFSET_ADJUSTMENT;

    const isNewTimeBlock = index === 0 || hours[index - 1] !== hour - 1;

    if (isNewTimeBlock) {
      timeBlocks.push({
        id: -2,
        dayPartId: 0,
        displayName: isFullDay ? 'Full Day' : '',
        startOffset,
        endOffset,
      });
    } else {
      timeBlocks[timeBlocks.length - 1].endOffset = endOffset;
    }

    return timeBlocks;
  }, []);
};

export const getDayEntry = ({
  dayId,
  date,
  dateString,
  dayParts,
  isFullDay,
  hours,
  dayOfTheWeek,
}: {
  dayId: number;
  date: Date;
  dateString: string;
  dayParts: DayPart[];
  isFullDay: boolean;
  hours: number[];
  dayOfTheWeek: Days;
}): SelectedDay => ({
  dayId,
  currentSelection: false,
  dayParts,
  date,
  id: dateString,
  isFullDay,
  tempCustomHours: {},
  dayOfTheWeek,
  hours,
});

export const transformNoRepeatPattern = ({
  startDate,
  endDate,
  entries,
}: {
  startDate: string;
  endDate: string;
  entries: DirectSalesNoRepeatPatternEntry[];
}): GroupOfSelectedDays => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const daysArray: GroupOfSelectedDays[] = [];

  let dayId = 0;

  const dateRange = eachDayOfInterval({ start, end });

  dateRange.forEach((date) => {
    const dayOfWeek = format(date, 'EEEE').toUpperCase();
    const dateString = format(date, "yyyy-MM-dd'T'00:00:00.000'Z'");

    const matchingDate = entries.find((pattern) => pattern.date === format(date, 'yyyy-MM-dd'));

    dayId++;

    if (matchingDate) {
      const { hours } = matchingDate;

      const isFullDay = hours.length === 0;
      const dayParts = groupHoursIntoTimeBlocks(hours);

      daysArray.push({
        [dateString]: getDayEntry({
          dayId,
          date,
          dateString,
          dayParts,
          isFullDay,
          hours,
          dayOfTheWeek: dayOfWeek as Days,
        }),
      });
    } else {
      daysArray.push({
        [dateString]: getDayEntry({
          dayId,
          date,
          dateString,
          dayParts: [],
          isFullDay: false,
          hours: [],
          dayOfTheWeek: dayOfWeek as Days,
        }),
      });
    }
  });

  return daysArray.reduce((acc, dayEntry) => ({ ...acc, ...dayEntry }), {});
};

export const transformRepeatedPattern = ({
  startDate,
  entries,
}: {
  startDate: string;
  entries: DirectSalesWeeklyPatternEntry[];
}): GroupOfSelectedDays => {
  const dealLineStartDate = new Date(startDate);

  const weeklyPatternDays = ALL_DAYS_OF_THE_WEEK.reduce<GroupOfSelectedDays>((acc, day, dayIndex) => {
    acc[day] = {
      dayId: dayIndex,
      id: dealLineStartDate.toISOString(),
      date: dealLineStartDate,
      currentSelection: false,
      isFullDay: false,
      dayParts: [],
      tempCustomHours: {},
      dayOfTheWeek: day,
      hours: [],
    };

    return acc;
  }, {});

  entries.forEach(({ day, hours }) => {
    const selectedHours = hours.length === 0 ? ALL_HOURS : hours;
    const isFullDay = selectedHours.length === 24;
    const dayparts = groupHoursIntoTimeBlocks(selectedHours);

    weeklyPatternDays[day].hours = selectedHours;
    weeklyPatternDays[day].isFullDay = isFullDay;
    weeklyPatternDays[day].dayParts = dayparts;
  });

  return weeklyPatternDays;
};

export const reverseTransformDaypartTargeting = (
  rangePattern: DirectSalesRangePattern,
  startDate: string,
  endDate: string,
): GroupOfSelectedDays => {
  if (rangePattern?.type === 'WEEKLY' && rangePattern.entries?.length > 0) {
    return transformRepeatedPattern({ startDate, entries: rangePattern.entries });
  }

  if (rangePattern?.type === 'NO_REPEAT' && rangePattern.entries?.length > 0) {
    return transformNoRepeatPattern({ startDate, endDate, entries: rangePattern.entries });
  }

  return {};
};
